import React, { useState, useEffect, useMemo } from 'react';
import Header from "../components/header";
import Slider from 'react-slider';
import '../css/rangeSlider.css';
import YadampChat from '../components/YadampChat';
import { FiSearch } from "react-icons/fi";

/* ======================= CONFIG / LABELS / BOUNDS ======================= */

const NUMBER_FIELDS_WITH_RANGE = [
  "lengths","dates",
  "molecularWeight","isoelectricPoint","deltaChargePh5Ph9",
  "chargeAtPh5","chargeAtPh7","chargeAtPh9",
  "helicity","hMomA","meanHmA","hMomB","meanHmB","hMomC","meanHmC",
  "deltaG","molecularVolume","flexibility","polarityIndex","instabilityIndex","bomanIndex",
  "A","C","D","E","F","G","H","I","K","L","M","N","P","Q","R","S","T","V","W","Y",
  "aaPos","aaNeg","aaPolar","aaHydrophobic","aaSpecial",
];

const FIELD_LABELS = {
  subName: "Name (contains)",
  subSequence: "Sequence (contains)",
  species: "Species (regex)",
  lengths: "Length [min, max]",
  dates: "Year [from, to]",
  comment: "Activity comment (regex)",
  phylum: "Phylum", class: "Class", order: "Order", family: "Family", genus: "Genus",
  molecularWeight: "Molecular weight [Da]",
  isoelectricPoint: "Isoelectric point [pI]",
  deltaChargePh5Ph9: "ΔCharge pH5→pH9",
  chargeAtPh5: "Charge at pH 5", chargeAtPh7: "Charge at pH 7", chargeAtPh9: "Charge at pH 9",
  helicity: "Helicity [%]",
  hMomA: "Hydrophobic moment A", meanHmA: "Mean HM A",
  hMomB: "Hydrophobic moment B", meanHmB: "Mean HM B",
  hMomC: "Hydrophobic moment C", meanHmC: "Mean HM C",
  deltaG: "ΔG",
  molecularVolume: "Molecular volume",
  flexibility: "Flexibility",
  polarityIndex: "Polarity index",
  instabilityIndex: "Instability index",
  bomanIndex: "Boman index",
  A:"A (Alanine)", C:"C (Cysteine)", D:"D (Aspartic acid)", E:"E (Glutamic acid)", F:"F (Phenylalanine)",
  G:"G (Glycine)", H:"H (Histidine)", I:"I (Isoleucine)", K:"K (Lysine)", L:"L (Leucine)",
  M:"M (Methionine)", N:"N (Asparagine)", P:"P (Proline)", Q:"Q (Glutamine)", R:"R (Arginine)",
  S:"S (Serine)", T:"T (Threonine)", V:"V (Valine)", W:"W (Tryptophan)", Y:"Y (Tyrosine)",
  aaPos:"AA positive", aaNeg:"AA negative", aaPolar:"AA polar", aaHydrophobic:"AA hydrophobic", aaSpecial:"AA special",
};

const RANGE_BOUNDS = {
  lengths:{min:0,max:100,step:1}, dates:{min:1900,max:new Date().getFullYear(),step:1},
  molecularWeight:{min:0,max:10000,step:1}, isoelectricPoint:{min:0,max:14,step:0.1},
  deltaChargePh5Ph9:{min:-30,max:30,step:1}, chargeAtPh5:{min:-20,max:20,step:1},
  chargeAtPh7:{min:-20,max:20,step:1}, chargeAtPh9:{min:-20,max:20,step:1},
  helicity:{min:0,max:100,step:1},
  hMomA:{min:0,max:5,step:0.01}, meanHmA:{min:0,max:2,step:0.01},
  hMomB:{min:0,max:5,step:0.01}, meanHmB:{min:0,max:2,step:0.01},
  hMomC:{min:0,max:5,step:0.01}, meanHmC:{min:0,max:2,step:0.01},
  deltaG:{min:-50,max:50,step:0.1}, molecularVolume:{min:0,max:20000,step:1},
  flexibility:{min:0,max:1,step:0.01}, polarityIndex:{min:0,max:100,step:1},
  instabilityIndex:{min:0,max:100,step:1}, bomanIndex:{min:-10,max:20,step:0.1},
  A:{min:0,max:50,step:1}, C:{min:0,max:50,step:1}, D:{min:0,max:50,step:1}, E:{min:0,max:50,step:1}, F:{min:0,max:50,step:1},
  G:{min:0,max:50,step:1}, H:{min:0,max:50,step:1}, I:{min:0,max:50,step:1}, K:{min:0,max:50,step:1}, L:{min:0,max:50,step:1},
  M:{min:0,max:50,step:1}, N:{min:0,max:50,step:1}, P:{min:0,max:50,step:1}, Q:{min:0,max:50,step:1}, R:{min:0,max:50,step:1},
  S:{min:0,max:50,step:1}, T:{min:0,max:50,step:1}, V:{min:0,max:50,step:1}, W:{min:0,max:50,step:1}, Y:{min:0,max:50,step:1},
  aaPos:{min:0,max:50,step:1}, aaNeg:{min:0,max:50,step:1}, aaPolar:{min:0,max:50,step:1}, aaHydrophobic:{min:0,max:50,step:1}, aaSpecial:{min:0,max:50,step:1},
};

// Range per attività batterica
const BACTERIA_RANGE = { min: 0, max: 1000, step: 0.1 };

/* ============================== HELPERS UI ============================== */

const StepDots = ({ total, current, onJump }) => (
  <div style={{ alignItems: 'center', textAlign: 'center' }}>
    {Array.from({ length: total }, (_, i) => (
      <button
        key={i}
        type="button"
        onClick={() => onJump(i)}
        aria-label={`Vai allo step ${i + 1}`}
        style={{
          width: 12, height: 12, margin: '15px',
          borderRadius: 999, border: '2px solid #0583B3',
          backgroundColor: i === current ? '#0583B3' : '#FFFFFF',
          boxShadow: '0 0 7px #0583B388', cursor: 'pointer', padding: 0
        }}
      />
    ))}
  </div>
);

function TextField({ name, label, value, onChange, placeholder }) {
  return (
    <>
      <label className="label">{label}</label>
      <input
        className="inputText" type="text"
        value={value ?? ""} placeholder={placeholder}
        onChange={(e) => onChange(name, e.target.value)}
      />
    </>
  );
}

function RangeSliderField({ name, label, value, onChange }) {
  const bounds = RANGE_BOUNDS[name] ?? { min: 0, max: 100, step: 1 };

  const sliderValue = useMemo(() => {
    const v0 = Number(value?.min), v1 = Number(value?.max);
    const min = Number.isFinite(v0) ? v0 : bounds.min;
    const max = Number.isFinite(v1) ? v1 : bounds.max;
    const lo = Math.max(bounds.min, Math.min(min, bounds.max));
    const hi = Math.max(lo, Math.min(max, bounds.max));
    return [lo, hi];
  }, [value, bounds]);

  return (
    <div className="rangeField" style={{ minWidth: 0 }}>
      <label className="label">{label}</label>
      <Slider
        className="slider"
        value={sliderValue}
        min={bounds.min} max={bounds.max} step={bounds.step}
        onChange={([lo, hi]) => onChange(name, { min: lo, max: hi })}
        pearling minDistance={bounds.step} style={{ width: "100%" }}
      />
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5 }}>
        <input
          type="number" className="inputSliderRange"
          step={bounds.step} min={bounds.min} max={bounds.max} value={sliderValue[0]}
          placeholder={`${bounds.min}`}
          onChange={(e) => {
            const v = Number(e.target.value);
            onChange(name, { min: Math.min(Math.max(v, bounds.min), sliderValue[1]), max: sliderValue[1] });
          }}
        />
        <input
          type="number" className="inputSliderRange"
          step={bounds.step} min={bounds.min} max={bounds.max} value={sliderValue[1]}
          placeholder={`${bounds.max}`}
          onChange={(e) => {
            const v = Number(e.target.value);
            onChange(name, { min: sliderValue[0], max: Math.max(Math.min(v, bounds.max), sliderValue[0]) });
          }}
        />
      </div>
    </div>
  );
}

function BacteriumRow({ value, onChangeName, onChangeRange, onRemove }) {
  const sliderValue = useMemo(() => {
    const v0 = Number(value?.min), v1 = Number(value?.max);
    const lo = Number.isFinite(v0) ? v0 : BACTERIA_RANGE.min;
    const hi = Number.isFinite(v1) ? v1 : BACTERIA_RANGE.max;
    return [
      Math.max(BACTERIA_RANGE.min, Math.min(lo, hi)),
      Math.min(BACTERIA_RANGE.max, Math.max(lo, hi))
    ];
  }, [value]);

  return (
    <div className="bacteriumRow" style={{ minWidth: 0, marginTop: -10 }}>
      <div className='space'/> 
      <div className="inputLeft" style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: 'center' }}>
        <button type="button" className="button dangerbutton" onClick={onRemove} style={{padding:'11px 15px', marginBottom: '3px'}}>
          x
        </button>
        <input
          type="text" className="inputText"
          placeholder="enter field name… (e.g. e_coli)"
          value={value.name ?? ""} onChange={(e) => onChangeName(e.target.value)}
        />
      </div>
      <div className="inputRight">
        <div className="bacteriumSliderCol" style={{ width: "100%", overflowX: "hidden" }}>
          <Slider
            className="slider"
            value={sliderValue}
            min={BACTERIA_RANGE.min} max={BACTERIA_RANGE.max} step={BACTERIA_RANGE.step}
            onChange={(vals) => onChangeRange({ min: vals[0], max: vals[1] })}
            pearling minDistance={BACTERIA_RANGE.step} style={{ width: "100%" }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5 }}>
            <input
              type="number" className="inputSliderRange"
              step={BACTERIA_RANGE.step} min={BACTERIA_RANGE.min} max={BACTERIA_RANGE.max}
              value={sliderValue[0]} placeholder={`${BACTERIA_RANGE.min}`}
              onChange={(e) => {
                const v = Number(e.target.value);
                onChangeRange({ min: Math.min(Math.max(v, BACTERIA_RANGE.min), sliderValue[1]), max: sliderValue[1] });
              }}
            />
            <input
              type="number" className="inputSliderRange"
              step={BACTERIA_RANGE.step} min={BACTERIA_RANGE.min} max={BACTERIA_RANGE.max}
              value={sliderValue[1]} placeholder={`${BACTERIA_RANGE.max}`}
              onChange={(e) => {
                const v = Number(e.target.value);
                onChangeRange({ min: sliderValue[0], max: Math.max(Math.min(v, BACTERIA_RANGE.max), sliderValue[0]) });
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

const JSONBlock = ({ data }) => (
  <pre className="jsonBlock" style={{ background:'#0b0b0b0a', padding:12, borderRadius:8, overflow:'auto' }}>
    {JSON.stringify(data ?? {}, null, 2)}
  </pre>
);

/* ============================== CARDS RISULTATI ============================== */

function YadampCard({ item }) {
  // Campi comuni con fallback
  const id = item.id ?? item.ID ?? item.peptide_id ?? item.pep_id ?? item.accession ?? item.code ?? null;
  const name = item.name ?? item.subName ?? item.peptide_name ?? item.title ?? (id ? `ID ${id}` : 'Unknown');
  const sequence = item.sequence ?? item.subSequence ?? item.seq ?? null;
  const species = item.species ?? item.organism ?? item.source ?? null;
  const length = item.length ?? item.len ?? (sequence ? sequence.length : null);
  const mw = item.molecularWeight ?? item.mw ?? item.mass ?? null;
  const pI = item.isoelectricPoint ?? item.pI ?? item.pi ?? null;

  return (
    <div className="cardYadamp">
      <div>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>{name}</div>
        {sequence && <p><strong>Sequence:</strong> {sequence}</p>}
        {species && <p><strong>Species:</strong> {species}</p>}
        {length != null && <p><strong>Length:</strong> {length}</p>}
        {mw != null && <p><strong>Molecular Weight:</strong> {mw}</p>}
        {pI != null && <p><strong>Isoelectric Point:</strong> {pI}</p>}
      </div>
    </div>
  );
}

/* ============================== PAYLOAD BUILD ============================== */

function buildPayload(state) {
  const body = { serviceName: "YADAMP_QUERY" };

  ["subName","subSequence","species","comment","phylum","class","order","family","genus"].forEach(k=>{
    if (state[k] && String(state[k]).length>0) body[k] = state[k];
  });

  if (state.bacteriumPairs && state.bacteriumPairs.length) {
    const names = [], ranges = [];
    state.bacteriumPairs.forEach(p => {
      if (p?.name && p.min !== '' && p.max !== '') {
        names.push(p.name);
        ranges.push([Number(p.min), Number(p.max)]);
      }
    });
    if (names.length) { body.bacteriumNames = names; body.activityValues = ranges; }
  }

  NUMBER_FIELDS_WITH_RANGE.forEach(k => {
    const v = state[k];
    if (v && v.min !== '' && v.max !== '' && !Number.isNaN(Number(v.min)) && !Number.isNaN(Number(v.max))) {
      body[k] = [Number(v.min), Number(v.max)];
    }
  });

  // Paginazione iniziale
  body.pageNum = 1;
  body.pageSize = 10;

  return body;
}

// Estrazione difensiva degli item dal payload di risposta
function extractItemsFromResponse(data) {
  if (!data) return [];
  if (Array.isArray(data.result)) return data.result;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.items)) return data.items;
  if (data.result && typeof data.result === 'object') return Object.values(data.result);
  return [];
}

/* ================================ PAGE ================================= */

export default function Yadamp() {
  // STEP management
  const [step, setStep] = useState(0);

  // Stato form completo
  const [state, setState] = useState({
    bacteriumPairs: [{ name: "", min: BACTERIA_RANGE.min, max: BACTERIA_RANGE.max }],
  });
  const [busy, setBusy] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);

  // risultati e paginazione
  const [items, setItems] = useState([]);
  const [pageNum, setPageNum] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [payloadBase, setPayloadBase] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(true);

  // toggle vista: false=form (bodyYadamp), true=risultati (yadampSearch)
  const [showResults, setShowResults] = useState(false);

  const setField = (name, value) => setState(s => ({ ...s, [name]: value }));
  const addBacterium = () => setState(s => ({...s, bacteriumPairs: [...(s.bacteriumPairs||[]), { name:"", min:BACTERIA_RANGE.min, max:BACTERIA_RANGE.max }]}));
  const removeBacterium = (idx) => setState(s => ({...s, bacteriumPairs: s.bacteriumPairs.filter((_,i)=>i!==idx)}));
  const updateBacterium = (idx, patch) => setState(s => ({...s, bacteriumPairs: s.bacteriumPairs.map((p,i)=> i===idx? {...p, ...patch} : p)}));

  const performRequest = async (payload) => {
    const res = await fetch("https://yadamp.softmining.it/ws.php", {
      method:"POST", headers:{ "Content-Type":"application/json" }, body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null); setResponse(null); setBusy(true);

    try {
      const payload = buildPayload(state);
      setPayloadBase(payload);
      setPageNum(1);

      const data = await performRequest(payload);
      setResponse(data);

      const firstBatch = extractItemsFromResponse(data);
      setItems(firstBatch);
      setHasMore(firstBatch.length > 0);

      // RIMPIAZZA bodyYadamp con yadampSearch
      setShowResults(true);
    } catch (err) {
      setError(String(err));
      setItems([]); setHasMore(false);
    } finally {
      setBusy(false);
    }
  };

  const handleLoadMore = async () => {
    if (!payloadBase || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const nextPage = pageNum + 1;
      const payload = { ...payloadBase, pageNum: nextPage, pageSize: payloadBase.pageSize ?? 10 };
      const data = await performRequest(payload);
      const batch = extractItemsFromResponse(data);
      if (batch.length === 0) {
        setHasMore(false);
      } else {
        setItems(prev => [...prev, ...batch]);
        setPageNum(nextPage);
        setHasMore(true);
      }
    } catch (err) {
      setError(String(err));
    } finally {
      setLoadingMore(false);
    }
  };

  const goToFirstStep = () => {
    setStep(0);
    // opzionale: riportiamo in alto lo scroll del pannello
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
  };

  

  /* ====================== STEPS (renderer) ====================== */
  const STEPS = [
    {
      title: "Identifiers",
      render: () => (
        <>
          <div className="inputLeft">
            <TextField name="subName" label={FIELD_LABELS.subName} value={state.subName} onChange={setField}
              placeholder="enter name… (e.g. magainin)" />
          </div>
          <div className="inputRight">
            <TextField name="subSequence" label={FIELD_LABELS.subSequence} value={state.subSequence} onChange={setField}
              placeholder="enter sequence… (e.g. GIGK)" />
          </div>

          <div className="inputLeft">
            <TextField name="species" label={FIELD_LABELS.species} value={state.species} onChange={setField}
              placeholder="enter species regex… (e.g. Odorrana)" />
          </div>
          <div className="inputRight inputRightNull"></div>

          <div className="space"/>
          <h2 className="label">Ranges [min, max]</h2>

          <div className="inputLeft">
            <RangeSliderField name="lengths" label={FIELD_LABELS.lengths} value={state.lengths} onChange={setField} />
          </div>
          <div className="inputRight">
            <RangeSliderField name="dates" label={FIELD_LABELS.dates} value={state.dates} onChange={setField} />
          </div>
        </>
      )
    },
    {
      title: "Bacterial activity",
      render: () => (
        <>
          <div style={{ marginBottom:'25px' }}>
            <TextField name="comment" label={FIELD_LABELS.comment} value={state.comment} onChange={setField}
              placeholder="enter activity comment… (e.g. Inactive against)" />
          </div>

          <div className="bacteriaHeader" style={{ display:"flex", justifyContent:"space-between", gap:12, alignItems: 'center' }}>
            <div><h2 className="label">Bacteria + range [min, max]</h2></div>
            <button type="button" onClick={addBacterium} className="buttonUser button">+ Add bacterium</button>
          </div>

          <div className="inputLeft" style={{ width:"100%" }}>
            <div className="bacteriaList" style={{ maxHeight: 175, overflowY: "auto", overflowX:"hidden" }}>
              {(state.bacteriumPairs||[]).map((p, idx)=>(
                <React.Fragment key={idx}>
                  <BacteriumRow
                    value={p}
                    onChangeName={(name)=>updateBacterium(idx,{ name })}
                    onChangeRange={({min,max})=>updateBacterium(idx,{ min, max })}
                    onRemove={()=>removeBacterium(idx)}
                  />
                  <div className="spaceGraph"/>
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="inputRight"></div>
        </>
      )
    },
    {
      title: "Taxonomy",
      render: () => (
        <>
          <div className="inputLeft">
            <TextField name="phylum" label={FIELD_LABELS.phylum} value={state.phylum} onChange={setField}
              placeholder="enter phylum… (e.g. Chordata)" />
          </div>
          <div className="inputRight">
            <TextField name="class" label={FIELD_LABELS.class} value={state.class} onChange={setField}
              placeholder="enter class… (e.g. Amphibia)" />
          </div>

          <div className="inputLeft">
            <TextField name="order" label={FIELD_LABELS.order} value={state.order} onChange={setField}
              placeholder="enter order… (e.g. Anura)" />
          </div>
          <div className="inputRight">
            <TextField name="family" label={FIELD_LABELS.family} value={state.family} onChange={setField}
              placeholder="enter family… (e.g. Ranidae)" />
          </div>

          <div className="inputLeft">
            <TextField name="genus" label={FIELD_LABELS.genus} value={state.genus} onChange={setField}
              placeholder="enter genus… (e.g. Odorrana)" />
          </div>
          <div className="inputRight"></div>
        </>
      )
    },
    {
      title: "Molecular descriptors",
      render: () => {
        const keys = [
          "molecularWeight","isoelectricPoint","deltaChargePh5Ph9","chargeAtPh5","chargeAtPh7","chargeAtPh9",
          "helicity","hMomA","meanHmA","hMomB","meanHmB","hMomC","meanHmC",
          "deltaG","molecularVolume","flexibility","polarityIndex","instabilityIndex","bomanIndex"
        ];
        const rows = [];
        for (let i=0; i<keys.length; i+=2) rows.push(keys.slice(i, i+2));
        return (
          <>
            {rows.map((pair, idx)=>(
              <React.Fragment key={idx}>
                <div className="inputLeft">
                  <RangeSliderField name={pair[0]} label={FIELD_LABELS[pair[0]]} value={state[pair[0]]} onChange={setField} />
                </div>
                <div className="inputRight">
                  {pair[1] ? <RangeSliderField name={pair[1]} label={FIELD_LABELS[pair[1]]} value={state[pair[1]]} onChange={setField} /> : null}
                </div>
                <div className="space"/>
              </React.Fragment>
            ))}
          </>
        );
      }
    },
    {
      title: "Individual AA Counts",
      render: () => {
        const keysCounts = [
          "A","C","D","E","F","G","H","I","K","L","M","N","P","Q","R","S","T","V","W","Y",
        ];
        const keysClasses = [
          "aaPos","aaNeg","aaPolar","aaHydrophobic","aaSpecial",
        ];
        const toPairs = (arr) => {
          const out = [];
          for (let i = 0; i < arr.length; i += 2) out.push(arr.slice(i, i + 2));
          return out;
        };
        const rowsCounts = toPairs(keysCounts);
        const rowsClasses = toPairs(keysClasses);

        return (
          <>
            {rowsCounts.map((pair, idx) => (
              <React.Fragment key={`cnt-${idx}`}>
                <div className="inputLeft">
                  <RangeSliderField
                    name={pair[0]}
                    label={FIELD_LABELS[pair[0]] || pair[0]}
                    value={state[pair[0]]}
                    onChange={setField}
                  />
                </div>
                <div className="inputRight">
                  {pair[1] ? (
                    <RangeSliderField
                      name={pair[1]}
                      label={FIELD_LABELS[pair[1]] || pair[1]}
                      value={state[pair[1]]}
                      onChange={setField}
                    />
                  ) : null}
                </div>
                <div className="space" />
              </React.Fragment>
            ))}

            <div className="space" />
            <h2 className="label">AA Classes</h2>

            {rowsClasses.map((pair, idx) => (
              <React.Fragment key={`cls-${idx}`}>
                <div className="inputLeft">
                  <RangeSliderField
                    name={pair[0]}
                    label={FIELD_LABELS[pair[0]] || pair[0]}
                    value={state[pair[0]]}
                    onChange={setField}
                  />
                </div>
                <div className="inputRight">
                  {pair[1] ? (
                    <RangeSliderField
                      name={pair[1]}
                      label={FIELD_LABELS[pair[1]] || pair[1]}
                      value={state[pair[1]]}
                      onChange={setField}
                    />
                  ) : null}
                </div>
                <div className="space" />
              </React.Fragment>
            ))}
          </>
        );
      }
    }
  ];

  const LAST_STEP = STEPS.length - 1;

  /* ============================== RENDER ============================== */

  // Responsive sidebar behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1100) {
        setIsChatOpen(true);   // su desktop sidebar sempre aperta
      } else {
        setIsChatOpen(false);  // su mobile parte chiusa
      }
    };
      
    handleResize(); // inizializza in base alla larghezza attuale
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <Header />
      <div className="riga headerTools">
        <h3 className="titolo titoloFeature">Yadamp</h3>
        <p className="text description descriptionSidebarTools">
          YADAMP (Yet Another Database of Antimicrobial Peptides) collects peptides with reported antimicrobial activity from literature sources. It provides data on activity against bacterial strains and supports queries by name, sequence, species, length, year of publication, and sequence motifs. The database also includes molecular descriptors such as molecular weight, isoelectric point, net charge, helicity, hydrophobic moment, molecular volume, flexibility, polarity index, instability index, Boman index, and presence of specific amino acids.
        </p>
        <div className="spaceGraph"></div>
        <button type="button" className="button sign-button sidebar-toggle" onClick={() => setIsChatOpen(prev => !prev)}>
          {isChatOpen ? 'Hide Yadamp Assistant' : 'Show Yadamp Assistant'}
        </button>                                                      
      </div>

      {/* Toggle: o bodyYadamp (form) oppure yadampSearch (risultati) */}
      {showResults ? (
        <div className="yadampSearch">
          <div style={{ display:"flex", justifyContent:"space-between", gap:12, alignItems: 'center' }}>
            <h3 className="sectionTitle" style={{ marginBottom: 10 }}>Results:</h3>
            <button
              type="button"
              className="buttonUser button"
              onClick={() => { setShowResults(false); goToFirstStep(); }}
            >
              <FiSearch style={{ marginRight: 10 }} /> Search again
            </button>
          </div>
          <hr style={{ width: '100%', margin: '0 0 15px 0' }} />

          {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}

          <div className="yadampSearchResults" style={{ display:'flex', flexWrap:'wrap', justifyContent:'center' }}>
            {items.map((item, idx) => {
              const key = item.id ?? item.ID ?? item.accession ?? item.code ?? `row-${idx}`;
              return <YadampCard key={key} item={item} />;
            })}
            {hasMore && (
            <div style={{ width:'100%', textAlign:'center', margin:'25px' }}>
              <button className="button buttonUser" onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? "Loading..." : "Load more"}
              </button>
            </div>
          )}
          </div>
        </div>
      ) : (
        <div className="bodyYadamp">
          <form onSubmit={handleSubmit} className="formRoot">
            <div className='formYadamp'>
              <h2 className="sectionTitle" style={{ marginTop:0 }}>{STEPS[step].title}</h2>
              {STEPS[step].render()}
            </div>

            <div className="spaceGraph"></div>
            <StepDots total={STEPS.length} current={step} onJump={setStep} />
            <div className="spaceGraph"></div>

            <div className="inputLeftMobile">
              <button
                type="button"
                className="sign-button button download-button"
                onClick={(e)=>{ e.preventDefault(); setStep(s=>Math.max(s-1,0)); }}
                disabled={step === 0}
                style={step === 0 ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              >
                Back
              </button>
            </div>

            <div className="inputRightMobile">
              {/* FIX 1: manteniamo due bottoni separati per evitare il submit involontario */}
              <button
                type="button"
                className="sign-button button"
                onClick={(e)=>{ e.preventDefault(); setStep(s=>Math.min(s+1,LAST_STEP)); }}
                style={step < LAST_STEP ? undefined : { display: 'none' }}
              >
                Next
              </button>

              <button
                type="submit"
                className="sign-button button"
                disabled={busy}
                style={step === LAST_STEP ? undefined : { display: 'none' }}
              >
                {busy ? "Sending..." : "Search"}
              </button>
            </div>

            <div className="spaceGraph"></div>
          </form>
        </div>
      )}

      {/* Colonna destra: chatbot sempre visibile */}
      <div className={`ChatbotAreaYadamp ${isChatOpen ? 'is-open' : ''}`}>
        <YadampChat />
      </div>
      <div className="spaceGraph"></div>
    </>
  );
}
